/**
 * Shared TipTap `editorProps` paste sanitization helpers.
 *
 * These are browser-only (they rely on `DOMParser`) and are intended to be
 * passed directly as `editorProps.transformPastedHTML` and
 * `editorProps.transformPastedText` in any TipTap editor instance.
 *
 * Keeping a single implementation prevents the two editors from drifting
 * when the sanitization logic is updated.
 */

/**
 * Allowlist of URL schemes that are safe to render or insert as links.
 * - https / http  — standard web links
 * - mailto        — email links
 * - tel           — phone-number links (RFC 3966)
 * - ftp           — FTP resource links
 *
 * Schemes NOT in the list (javascript:, data:, vbscript:, etc.) are rejected
 * so that stored or pasted content cannot execute arbitrary code.
 */
const SAFE_HREF_RE = /^(https?|mailto|tel|ftp):/i;

/**
 * Returns true when the given href has a scheme that is safe to render or
 * navigate to. Whitespace is collapsed before the check to prevent trivial
 * bypass via leading/embedded spaces.
 */
export function isSafeHref(href: string): boolean {
  return SAFE_HREF_RE.test(href.replace(/\s/g, ''));
}

// True when click target is inside a safe external anchor (not `#`).
export function isClickedSafeExternalAnchor(target: HTMLElement): boolean {
  const anchor = target.closest('a[href]');
  if (!anchor) return false;
  const href = anchor.getAttribute('href');
  return !!(href && href !== '#' && isSafeHref(href));
}

export function transformPastedHTML(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('[style]').forEach((el) => el.removeAttribute('style'));
  doc.querySelectorAll('img').forEach((el) => el.remove());
  doc.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((el) => {
    const p = doc.createElement('p');
    p.innerHTML = el.innerHTML;
    el.replaceWith(p);
  });
  // Strip href attributes whose scheme is not in the safe allowlist so that
  // pasted content cannot introduce javascript:, data:, vbscript:, etc. links.
  // Also upgrade http:// → https:// on pasted links.
  doc.querySelectorAll('a[href]').forEach((el) => {
    const href = el.getAttribute('href') ?? '';
    if (!isSafeHref(href)) {
      el.removeAttribute('href');
    } else if (/^http:/i.test(href)) {
      el.setAttribute('href', href.replace(/^http:/i, 'https:'));
    }
  });
  return doc.body.innerHTML;
}

export function transformPastedText(text: string): string {
  return text.replace(/<[^>]+>/g, '');
}
